import { useState, type FormEvent } from "react";
import { CloudMast } from "./CloudMast";
import { AGREEMENT_POINTS } from "../lib/agreement";
import { CLOUD_ENGINES } from "../lib/engines.generated";

// The start / agreement screen. After the access code clears Landing, this
// is where the user agrees before the wizard opens.
//
// Survivor-first ordering (2026-06-10 redesign): the page answers questions
// in the order a lay user asks them — what do I need (the file card, up
// top), will this hurt me / how long / who decides (four short ✓ facts),
// then the agreement and Begin. The old three-column info wall and the
// "Before you start / Afterward" paragraphs are folded into the facts and
// the Private "more" expansion.
//
// The "I agree" gate is the hard one: it links to the real /terms and
// /privacy pages and is the user's affirmative acceptance, not a soft
// "I understand." Keep it that way.

// The public demo viewer — the same self-contained, plaintext "Demo case"
// build served at plaintosee.cc/sample. A demo carries no private content, so
// it ships unencrypted (no passphrase): a published passphrase is friction
// with no protection. Real analyses are sealed with a key only the recipient
// holds — that assurance is stated in copy, not demonstrated by gating the demo.
const SAMPLE_HTML_URL = "/sample/aurora-sample.html";

type Props = {
  // Async: records the clickwrap acceptance before the wizard opens. Rejects
  // if the accept POST fails, so Welcome can surface an inline retry instead
  // of advancing.
  onStart: () => Promise<void>;
  // Resolved by Shell's single /config fetch on mount (see Shell.tsx) and
  // handed down as a prop — Welcome has no fetch of its own. Once the
  // public self-serve door is open, the "Early access" mast pill should not
  // show here either (2026-07-04: "public should not say early access
  // either once public switched"). Defaults false — the same early-access
  // fallback Shell itself uses before its fetch resolves / if it fails —
  // so existing callers that don't pass it keep today's look.
  selfServeOpen?: boolean;
  // Escapes back to Landing, losslessly — the access code and welcomed
  // flag stay exactly as stored, only the screen swaps (2026-07-04: a
  // returning user with a saved code had NO way back to Landing — the mast
  // logo wasn't a link and browser Back left the site outright). Optional
  // so a caller that doesn't need the escape hatch (none today) still
  // renders a plain, non-interactive brand mark.
  onGoHome?: () => void;
};

export function Welcome({ onStart, selfServeOpen = false, onGoHome }: Props) {
  const [ack, setAck] = useState(false);
  const [sampleRevealed, setSampleRevealed] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptError, setAcceptError] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ack || submitting) return;  // button is disabled but defense-in-depth
    setSubmitting(true);
    setAcceptError(false);
    try {
      await onStart();
    } catch {
      setAcceptError(true);
      setSubmitting(false);
    }
  }

  const canStart = ack;

  return (
    <main className="landing">
     <div className="landing-wrap">
      <CloudMast hideStatus={selfServeOpen} onBrandClick={onGoHome} />

      <section className="start">
        <div className="landing-eyebrow">You're in</div>
        <h1 className="landing-title">
          First: do you have your messages file?
        </h1>

        <div className="start-need">
          <div className="start-need-kicker">You'll need one thing</div>
          <h2 className="start-need-title">Your messages, saved as a file</h2>
          <ul className="start-need-formats">
            {CLOUD_ENGINES.map((e) => (
              <li key={e.id}>
                {e.display}
                <span>{e.exportLabel}</span>
              </li>
            ))}
            <li>
              A spreadsheet
              <span>CSV file of messages</span>
            </li>
          </ul>
          <button
            type="button"
            className="start-more"
            onClick={() => setHowOpen((v) => !v)}
            aria-expanded={howOpen}
          >
            {howOpen ? "Hide this" : "How do I get this file from my app? ▸"}
          </button>
          {howOpen ? (
            /* Engine-agnostic on purpose: the cards above (derived from the
               catalog) already name each app's exact export, and per-engine
               how-tos live in the drop step's format help. Hand-written
               enumerations here drifted once already. */
            <div className="start-expand" role="status">
              <p>
                Each app listed above has an export or download option that
                saves your messages as a file — the list names the exact
                report to look for, and the next step shows where to find it
                in your app. If your messages live somewhere else, a
                spreadsheet works too: one row per message, with the date,
                who sent it, and what it said.
              </p>
            </div>
          ) : null}
        </div>

        <ul className="start-facts">
          <li>
            <span className="start-fact-mark" aria-hidden="true">✓</span>
            <span>
              <strong>Usually 15–30 minutes.</strong> Start it, save your
              passphrase, and we email you when it's ready.
            </span>
          </li>
          <li>
            <span className="start-fact-mark" aria-hidden="true">✓</span>
            <span>
              <strong>One sitting to start.</strong> Getting it going takes
              about 15 minutes, and your code works once.
            </span>
          </li>
        </ul>

        <section className="start-agreement" aria-label="The agreement">
          <div className="start-agreement-kicker">The agreement</div>
          <ol className="start-agreement-list">
            {AGREEMENT_POINTS.map((p) => (
              <li key={p.title}>
                <strong>{p.title}</strong> {p.body}
              </li>
            ))}
          </ol>
        </section>

        <form className="start-agree" onSubmit={handleSubmit} noValidate>
          <label className="start-ack">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
            />
            <span>
              I have read and agree to the points above, and to the{" "}
              <a href="/terms" target="_blank" rel="noreferrer">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
              . I understand Aurora is not legal advice.
            </span>
          </label>

          <div className="start-actions">
            <button
              type="button"
              className="welcome-sample-cta"
              onClick={() => setSampleRevealed((v) => !v)}
              aria-expanded={sampleRevealed}
            >
              {sampleRevealed
                ? "Hide the example"
                : "Show me a finished example first →"}
            </button>
            <button
              type="submit"
              className="apply-submit"
              disabled={!canStart || submitting}
            >
              {submitting ? "One moment…" : "I have my file — begin →"}
            </button>
          </div>

          {acceptError ? (
            <p className="start-accept-error" role="alert">
              We couldn't record your agreement just now. Please try again.
            </p>
          ) : null}

          {sampleRevealed ? (
            <div className="welcome-sample-panel" role="status">
              <p className="welcome-sample-line">
                <a href={SAMPLE_HTML_URL} target="_blank" rel="noreferrer">
                  View the sample analysis
                </a>{" "}
                — a finished brief that opens right in your browser.
              </p>
              <p className="welcome-sample-note">
                Synthetic case — all names, dates, and incidents are invented.
                Same shape as the packet you'll get back.
              </p>
            </div>
          ) : null}
        </form>
      </section>

      <footer className="landing-foot">
        <div className="landing-foot-left">Aurora · Built independently.</div>
        <div className="landing-foot-right">&copy; 2026 Aurora</div>
      </footer>
     </div>
    </main>
  );
}
